import React, { useEffect } from 'react';
import '../../index.css';
import { CurrencyProvider } from '../../contexts/CurrencyContext';
import { startGaPageviewTracking } from '../../utils/gaGuard';
import { startAdTagTracking } from '../../utils/adTags';
import ConsentBanner from '../components/ConsentBanner';
import AppBannerSessionHeartbeat from '../components/AppBannerSessionHeartbeat';

/**
 * GA4-01: the app's only page-view sender.
 *
 * `+Head.tsx` now boots GA4 with `send_page_view: false`, so nothing is
 * measured until this runs. It mounts above both client routers — Vike's
 * between server-rendered pages, React Router's inside src/pages/[[catchAll]] —
 * which is what lets one subscription cover every navigation the app can make.
 *
 * Renders nothing, and does nothing during SSR: the effect is client-only, and
 * the guard it delegates to reads the live `window.location` on every hit, so a
 * blocked route never sends and leaving one resumes measurement immediately.
 */
function GaPageviews(): null {
  useEffect(() => startGaPageviewTracking(), []);
  return null;
}

/**
 * GA4-14 spoke 3 — the advertising page views, started here and only here.
 *
 * ORDER MATTERS, and it is the render order below rather than anything in this
 * function: `GaPageviews` mounts first, so `startGaPageviewTracking` has
 * patched `history` and published its route-change signal before this
 * subscribes to it. Mounted as its own component for the same reason the
 * tracker is — so the two effects are independent and either can be removed
 * without disturbing the other.
 *
 * This is a CLIENT-ONLY effect in the layout, deliberately, and not a script in
 * `+Head.tsx`: no advertising tag may be emitted server-side. The head bootstrap
 * is untouched by this item.
 *
 * GA4-14 spoke 3c: what it starts is an IMAGE BEACON layer, not a script layer.
 * `utils/adTags.ts` injects nothing into this document — no `fbevents.js`, no
 * second gtag.js, no `<script>` of any kind — and issues at most one image
 * request per vendor per advertisable page view, synchronously, from the
 * route-change signal this component's sibling publishes.
 *
 * It runs on every route the web app can render, including the ones behind
 * React Router inside `src/pages/[[catchAll]]`. Which of them may actually
 * carry an ad tag is `ADS_ELIGIBLE_PATH_PREFIXES`'s decision, re-read on every
 * navigation — not this mount point's.
 */
function AdTagPageviews(): null {
  useEffect(() => startAdTagTracking(), []);
  return null;
}

/*
 * GA4-02: `startGaPageviewTracking` no longer measures anybody on its own. The
 * tracker still runs on every page and still decides WHICH navigations are
 * eligible, but the hit it wants to send is refused at the bootstrap gate until
 * the visitor has accepted the banner below — so on a first visit it computes a
 * page view, finds no tag and no consent, and sends nothing. Accepting is what
 * boots gtag.js and makes good the one page view that was owed.
 */

/**
 * CURR-01: CurrencyProvider is mounted here as well as in App.tsx.
 *
 * The two are not redundant. A direct load of /property/:slug or /for-sale/:type
 * is answered by the Vike SSR page and never mounts App.tsx, so without this the
 * currency toggle would exist only after a client-side navigation from Home.
 * The provider renders KES-only and hides the toggle until the rate loads, so it
 * is inert during SSR and adds nothing to the server render.
 */
export default function Layout({ children }: { children: React.ReactNode }) {
  return (
    <CurrencyProvider>
      <GaPageviews />
      <AdTagPageviews />
      {/* GROWTH-01: the web-to-app banner's session clock. Same reasoning as
          the two trackers above it — the layout is the one place that runs on
          every route, and a session has to be kept alive on the routes that
          carry no banner too, or a dismissal on a listing followed by forty
          minutes on the home page would count as two visits. Renders null. */}
      <AppBannerSessionHeartbeat />
      <div className="min-h-screen bg-brand-bg text-brand-text font-sans selection:bg-brand-highlight selection:text-white">
        {children}
      </div>
      {/* GA4-02. Mounted here — above both client routers, like the tracker —
          so one banner covers every surface the web app can render, including
          the React Router screens inside src/pages/[[catchAll]] which never get
          a second server render. Last in the subtree so that, at equal z-index,
          the consent gate is still the thing painted on top. It renders null on
          the server and on the first client render; see the file. */}
      <ConsentBanner />
    </CurrencyProvider>
  );
}
